// Matching household members by email address. Each of us can sign in and forward
// from the main address or any of the other addresses.

import type { Person, PersonId, Segment } from './types.ts';

/** Every address a member signs in or forwards from, lowercased. */
export function memberEmails(p: Person): string[] {
  return [p.email, ...(p.otherEmails ?? [])].filter((e): e is string => !!e).map((e) => e.trim().toLowerCase());
}

const TITLES = new Set(['mr', 'mrs', 'ms', 'miss', 'mstr', 'dr', 'mx']);

/** Lowercase name parts without titles: "TAN/MICHAEL LEI MR" -> ['tan', 'michael', 'lei']. */
function nameParts(name: string): string[] {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z]+/)
    .filter((p) => p && !TITLES.has(p));
}

/**
 * The member a traveler name on a booking refers to: the first and last parts of one of their
 * booking names appear in it (any order, so airline "LAST/FIRST MIDDLE" works), or it is just
 * their display name (typed by hand).
 */
export function memberForTraveler<P extends Pick<Person, 'name' | 'bookingNames'>>(members: P[], traveler: string): P | undefined {
  const parts = nameParts(traveler);
  if (parts.length === 0) return undefined;
  const has = new Set(parts);
  const covers = (bookingName: string) => {
    const p = nameParts(bookingName);
    const need = p.length > 2 ? [p[0], p[p.length - 1]] : p;
    return need.length > 0 && need.every((x) => has.has(x));
  };
  return (
    members.find((m) => m.bookingNames.some(covers)) ??
    members.find((m) => nameParts(m.name).join(' ') === parts.join(' '))
  );
}

/** "SMITH/SAM MR" or "Sam Smith" -> "Sam": how someone else on a booking is shown. */
export function firstName(printed: string): string {
  const given = printed.includes('/') ? printed.split('/')[1] : printed;
  const parts = given.trim().split(/\s+/).filter((p) => p && !TITLES.has(p.toLowerCase().replace(/\./g, '')));
  const first = parts[0] ?? printed.trim();
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

export interface Going {
  /** Which of us, in household order. */
  people: PersonId[];
  /** First names of anyone else printed on the booking. */
  others: string[];
  /** False when nothing says who's going: no pick and no names printed. */
  known: boolean;
}

/** Who's going on a booking: our pick if made, else the printed names matched to us. */
export function whoIsGoing(seg: Pick<Segment, 'who' | 'travelers'>, members: Array<Pick<Person, 'id' | 'name' | 'bookingNames'>>): Going {
  const matched = new Set<PersonId>();
  const others: string[] = [];
  for (const t of seg.travelers ?? []) {
    const m = memberForTraveler(members, t);
    if (m) matched.add(m.id);
    else if (t.trim()) others.push(firstName(t));
  }
  const picked = seg.who?.length ? new Set(seg.who) : matched;
  const people = members.map((m) => m.id).filter((id) => picked.has(id));
  return { people, others: [...new Set(others)], known: people.length > 0 || others.length > 0 };
}

/** The member who owns this address, if any. */
export function memberByEmail(members: Person[], address: string | undefined | null): Person | undefined {
  const addr = address?.trim().toLowerCase();
  if (!addr) return undefined;
  return members.find((m) => memberEmails(m).includes(addr));
}
